import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// OpenLayers
import { Style } from 'ol/style';

// MUI
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Panel, PanelGroup } from 'react-resizable-panels';

// Ui
import DataCentricPrimaryTable from '@/ui/DataCentric/DataCentricPrimaryTable';
import DataCentricSecondaryTable from '@/ui/DataCentric/DataCentricSecondaryTable';
import DataCentricMiniMap from '@/ui/DataCentric/DataCentricMiniMap';
import DataCentricImagesOverlay from '@/ui/DataCentric/DataCentricImagesOverlay';
import GeneralDialog from '../Dialog/GeneralDialog';

// Lib
import DataController from '@/lib/DataController';
// import useWindowSize from '@/components/useWindowSize';

// Context
import { IDialogProps } from '@/context/DialogContext/DialogContext';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { IModel } from '@/@types/models/model';
import { AllowRowActionFn, RowActionFn } from '@/@types/ui/Table';
import StyledPanelResizeHandle from '../PanelResize/StyledPanelResizeHandle';
import { Record } from '@/@types/lib/api';

export type DialogFormGenerateFunction = (record: DCRecord) => string;

export type DataCentricPageParams = {
  primaryModel: IModel;
  primaryTitleToken: string;
  primaryView: string | undefined;
  primaryAllowAdd?: boolean;
  primaryAllowView?: boolean;
  primaryAllowEdit?: boolean;
  primaryAllowDelete?: boolean;
  primaryAllowExport?: boolean;
  primaryAllowExportShape?: boolean;
  primaryAllowExportDxf?: boolean;
  primaryAllowDocumentButton?: boolean;
  primaryDialogComponent?: React.FunctionComponent<IDialogProps>;
  primaryDialogForm?: string | DialogFormGenerateFunction;
  primaryInitialSelectedId?: number;
  primaryQueryAttribute?: string; // attribute used for fetch in secondary table. by default its id, but we can override with this
  primaryAllowShowDeleted?: boolean;
  primaryAllowShow3DButton?: boolean;

  onCustomTableAction?: RowActionFn;
  allowCustomTableAction?: AllowRowActionFn;

  secondaryModel: IModel;
  secondaryTitleToken: string;
  secondaryView: string | undefined;
  secondaryAllowView?: boolean;
  secondaryAllowAdd?: boolean;
  secondaryAllowEdit?: boolean;
  secondaryAllowDelete?: boolean;
  secondaryDialogComponent?: React.FunctionComponent<IDialogProps>;
  secondaryDialogForm?: string | DialogFormGenerateFunction;
  secondaryAllowColumnPicker?: boolean;
  secondaryAllowFilter?: boolean;
  secondaryAllowExport?: boolean;
  secondaryAllowShowDeleted?: boolean;
  secondaryAllowDocumentButton?: boolean;

  secondaryOnCustomTableAction?: RowActionFn;
  secondaryAllowCustomTableAction?: AllowRowActionFn;

  autoSelectSecondaryFromMapFeatureId?: boolean;

  baseRecordPath: string;
  secondaryBaseRecordPath?: string; // if undefined, baseRecordPath will be used instead
  childRecordRelativePath: string;
  geomRelativePath: string;

  mapId: number;
  mapModel: IModel;
  allowMinimapHideObjects?: boolean;
  mapLayerStyle?: (f: any) => Style[];

  allowShowImages?: boolean;
  imageRelativePath?: string;
  modelPhotos?: IModel;

  allowPrimaryModelActivate?: boolean;
  allowShowSecondaryOnMap?: boolean;
  replacePrimaryIdInSecondaryPath?: boolean;
  geometryChangeType?: 'LineString' | 'Polygon' | 'Point';

  // Open page as dialog
  primaryDialogAsPagePath?: string | undefined;
  tableIdentifier?: string;
  foreignKeyField?: string;

  refreshPriamryOnSecondaryRefresh?: boolean;
};

const DataCentricPage = (props: DataCentricPageParams) => {
  const {
    primaryModel,
    primaryView,
    primaryTitleToken,
    primaryAllowAdd,
    primaryAllowEdit,
    primaryAllowView,
    primaryAllowDelete,
    primaryAllowExport,
    primaryAllowExportShape,
    primaryAllowExportDxf,
    primaryAllowDocumentButton,
    primaryDialogComponent = GeneralDialog,
    primaryDialogForm,
    primaryInitialSelectedId,
    secondaryModel,
    secondaryView,
    secondaryTitleToken,
    secondaryAllowView,
    secondaryAllowAdd,
    secondaryAllowEdit,
    secondaryAllowDelete,
    secondaryDialogComponent = GeneralDialog,
    secondaryDialogForm,
    secondaryAllowDocumentButton,
    mapId,
    mapModel,
    mapLayerStyle,
    baseRecordPath,
    childRecordRelativePath,
    allowShowImages,
    imageRelativePath,
    modelPhotos,
    primaryDialogAsPagePath,
    onCustomTableAction,
    allowCustomTableAction,
    primaryQueryAttribute,
    secondaryBaseRecordPath,
    allowMinimapHideObjects = true,
    tableIdentifier,
    allowPrimaryModelActivate = false,
    allowShowSecondaryOnMap = false,
    replacePrimaryIdInSecondaryPath = false,
    autoSelectSecondaryFromMapFeatureId = false,
    geometryChangeType,
    foreignKeyField,
    secondaryAllowColumnPicker,
    secondaryAllowFilter,
    secondaryAllowExport,
    refreshPriamryOnSecondaryRefresh,
    primaryAllowShowDeleted,
    primaryAllowShow3DButton,
    secondaryAllowShowDeleted,
    secondaryOnCustomTableAction,
    secondaryAllowCustomTableAction,
  } = props;

  const [selectedPrimaryRecordId, setSelectedPrimaryRecordId] = useState(primaryInitialSelectedId || 0);
  const [selectedPrimaryQueryAttribute, setSelectedPrimaryQueryAttribute] = useState<number | string | null>(null);
  const [selectedSecondaryRecordId, setSelectedSecondaryRecordId] = useState(0);
  const [showImages, setShowImages] = useState(false);
  const [images, setImages] = useState<DCRecord[]>([]);
  const [imagesButtonEnabled, setImagesButtonEnabled] = useState(false);
  const [refreshSecondaryrecords, setRefreshSecondaryRecords] = useState(false);
  const [refreshPrimaryRecords, setRefreshPrimaryrecords] = useState(true);

  const [alternateView, setAlternateView] = useState(false);

  // const windowSize = useWindowSize();

  const mapRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [geomChanged, setGeomChanged] = useState(false);

  const [searchParams] = useSearchParams();
  const primaryId = parseInt(searchParams.get('id') || '0', 10);

  const handlePrimaryRecordSelect = (id: number, queryAttribute?: number | string | null) => {
    if (id === selectedPrimaryRecordId) {
      setSelectedPrimaryRecordId(0);
      setSelectedSecondaryRecordId(0);
      if (primaryQueryAttribute) {
        setSelectedPrimaryQueryAttribute(null);
      }
    } else if (id !== 0 && id !== null) {
      // Primary changed, clear secondary selection so old secondary highlight does not persist.
      setSelectedSecondaryRecordId(0);
      setSelectedPrimaryRecordId(id);
      scrollIntoMap();
      if (primaryQueryAttribute) {
        setSelectedPrimaryQueryAttribute(queryAttribute ?? null);
      }
    }
  };

  const handleMapRecordSelect = (id: number | null, featureProperties?: Record) => {
    handlePrimaryRecordSelect(id ?? 0);

    if (autoSelectSecondaryFromMapFeatureId && id && featureProperties) {
      setTimeout(() => {
        setSelectedSecondaryRecordId(featureProperties.id as number);
      }, 300);
    }
  };

  const scrollIntoMap = () => {
    if (!mdUp && mapRef.current) {
      mapRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleMapResize = () => {
    setAlternateView((prev) => !prev);
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.key === 'l' || event.key === 'L') && event.altKey) {
        handleMapResize();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (!Number.isNaN(primaryId) && primaryId > 0) {
      setSelectedPrimaryRecordId(primaryId);
    }
  }, [primaryId]);

  const scrollIntoPrimary = () => {
    if (!mdUp && primaryRef.current) {
      primaryRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleSecondaryRecordSelect = (id: number) => {
    if (id === selectedSecondaryRecordId) {
      setSelectedSecondaryRecordId(0);
    } else if (id !== 0 && id !== null) {
      setSelectedSecondaryRecordId(id);
      scrollIntoMap();
    }
  };

  const handlePrimaryRecordsLoad = (records: Array<DCRecord>) => {
    if (primaryId) {
      records.sort((a, b) => {
        if (a.id === primaryId) return -1;
        if (b.id === primaryId) return 1;
        return 0;
      });
      // console.log(records)
    }
    setRefreshSecondaryRecords(true);
  };

  const handleSecondaryRecordsLoad = () => {};

  const fetchImages = () => {
    if (selectedPrimaryRecordId !== 0 && modelPhotos && imageRelativePath) {
      const dc = new DataController(modelPhotos);
      const customPath = `${baseRecordPath}/${selectedPrimaryRecordId}/${imageRelativePath}`;
      dc.GetData(customPath)
        .then((resp) => {
          if (resp.success && resp.data) {
            if (Array.isArray(resp.data)) {
              setImages(resp.data);
              setImagesButtonEnabled(true);
            } else {
              setImages([]);
              setImagesButtonEnabled(false);
            }
          }
        })
        .catch(() => {
          setImages([]);
          setImagesButtonEnabled(false);
        });
    }
  };

  useEffect(() => {
    if (primaryInitialSelectedId) {
      setSelectedPrimaryRecordId(primaryInitialSelectedId);
    }
  }, [primaryInitialSelectedId]);

  useEffect(() => {
    fetchImages();
    scrollIntoPrimary();
  }, [selectedPrimaryRecordId]);

  const setAllowImages = () => {
    setShowImages(!showImages);
  };

  // const upperPartHeight = windowSize.height ? `${Math.floor((windowSize.height - 52) * 0.6)}px` : '60%';
  // const lowerPartHeight = windowSize.height ? `${Math.floor((windowSize.height - 52) * 0.4)}px` : '40%';

  return !(mdUp && alternateView) ? (
    <Grid container columns={24} height="calc( 100vh - 48px )">
      <Grid
        size={24}
        sx={{ maxHeight: '60%', minHeight: { xs: allowShowImages && showImages ? '60%' : '0', md: '60%' } }}
        position="relative"
      >
        <div style={{ width: '100%', height: '100%' }} ref={primaryRef}>
          <DataCentricPrimaryTable
            model={primaryModel}
            titleToken={primaryTitleToken}
            view={primaryView}
            baseRecordPath={baseRecordPath}
            onLoad={handlePrimaryRecordsLoad}
            onRowAction={onCustomTableAction}
            allowRowAction={allowCustomTableAction}
            onRecordSelect={handlePrimaryRecordSelect}
            selectedRecordId={selectedPrimaryRecordId}
            setAllowImages={setAllowImages}
            imagesEnabled={imagesButtonEnabled}
            allowShowImages={allowShowImages}
            allowAdd={primaryAllowAdd}
            allowView={primaryAllowView}
            allowEdit={primaryAllowEdit}
            allowDelete={primaryAllowDelete}
            allowExport={primaryAllowExport}
            allowExportShape={primaryAllowExportShape}
            allowExportDxf={primaryAllowExportDxf}
            allowDocumentButton={primaryAllowDocumentButton}
            allowShow3D={primaryAllowShow3DButton}
            dialogComponent={primaryDialogComponent}
            dialogForm={primaryDialogForm}
            dialogAsPagePath={primaryDialogAsPagePath}
            queryAttribute={primaryQueryAttribute}
            tableIdentifier={tableIdentifier ? `${tableIdentifier}_primary` : undefined}
            allowPrimaryModelActivate={allowPrimaryModelActivate}
            geometryChangeType={geometryChangeType}
            mapId={mapId}
            onGeomChange={() => setGeomChanged(true)}
            triggerRefresh={refreshPrimaryRecords}
            setTriggerRefresh={setRefreshPrimaryrecords}
            allowShowDeleted={primaryAllowShowDeleted}
          />
          {allowShowImages && showImages ? (
            <DataCentricImagesOverlay
              closeOverlay={() => setShowImages(false)}
              images={images}
              selectedObjektId={selectedPrimaryRecordId}
            />
          ) : null}
        </div>
      </Grid>

      <Grid sx={{ height: '40%' }} size={{ xs: 24, md: 9, lg: 7, xl: 5 }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}>
          <DataCentricMiniMap
            onPrimaryRecordSelect={handleMapRecordSelect}
            selectedPrimaryRecordId={selectedPrimaryRecordId}
            model={mapModel}
            mapId={mapId}
            allowHideObjects={allowMinimapHideObjects}
            selectedLayerStyle={mapLayerStyle}
            isResized={alternateView}
            onResize={handleMapResize}
            {...(allowShowSecondaryOnMap && {
              onSecondaryRecordSelect: handleSecondaryRecordSelect,
              selectedSecondaryRecordId,
              secondaryModel,
              replacePrimaryIdInSecondaryPath,
            })}
            onGeomChange={() => setGeomChanged(false)}
            refreshMap={geomChanged}
          />
        </div>
      </Grid>
      <Grid sx={{ maxHeight: { xs: '60%', md: '40%' }, minHeight: { xs: '0', md: '40%' } }} size={{ xs: 24, md: 15, lg: 17, xl: 19 }}>
        <DataCentricSecondaryTable
          selectedPrimaryRecordId={selectedPrimaryRecordId}
          selectedRecordId={autoSelectSecondaryFromMapFeatureId ? selectedSecondaryRecordId : undefined}
          selectedPrimaryRecordQueryAttribute={primaryQueryAttribute ? selectedPrimaryQueryAttribute : undefined}
          onRecordSelect={handleSecondaryRecordSelect}
          onLoad={handleSecondaryRecordsLoad}
          view={secondaryView}
          model={secondaryModel}
          titleToken={secondaryTitleToken}
          baseRecordPath={secondaryBaseRecordPath ?? baseRecordPath}
          childRecordRelativePath={childRecordRelativePath}
          triggerRefresh={refreshSecondaryrecords}
          setTriggerRefresh={setRefreshSecondaryRecords}
          allowView={secondaryAllowView}
          allowAdd={secondaryAllowAdd}
          allowEdit={secondaryAllowEdit}
          allowDelete={secondaryAllowDelete}
          allowDocumentButton={secondaryAllowDocumentButton}
          dialogComponent={secondaryDialogComponent}
          dialogForm={secondaryDialogForm}
          tableIdentifier={tableIdentifier ? `${tableIdentifier}_secondary` : undefined}
          foreignKeyValue={selectedPrimaryRecordId && foreignKeyField ? { [foreignKeyField]: selectedPrimaryRecordId } : {}}
          allowColumnPicker={secondaryAllowColumnPicker}
          allowFilter={secondaryAllowFilter}
          allowExport={secondaryAllowExport}
          setTriggerRefreshPrimary={refreshPriamryOnSecondaryRefresh ? setRefreshPrimaryrecords : undefined}
          allowShowDeleted={secondaryAllowShowDeleted}
          onRowAction={secondaryOnCustomTableAction}
          allowRowAction={secondaryAllowCustomTableAction}
        />
      </Grid>
    </Grid>
  ) : (
    <PanelGroup direction="horizontal">
      <Panel defaultSize={40} minSize={30}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}>
          <DataCentricMiniMap
            onPrimaryRecordSelect={handleMapRecordSelect}
            selectedPrimaryRecordId={selectedPrimaryRecordId}
            model={mapModel}
            mapId={mapId}
            allowHideObjects={allowMinimapHideObjects}
            selectedLayerStyle={mapLayerStyle}
            isResized={alternateView}
            onResize={handleMapResize}
            {...(allowShowSecondaryOnMap && {
              onSecondaryRecordSelect: handleSecondaryRecordSelect,
              selectedSecondaryRecordId,
              secondaryModel,
              replacePrimaryIdInSecondaryPath,
            })}
            onGeomChange={() => setGeomChanged(false)}
            refreshMap={geomChanged}
          />
        </div>
      </Panel>
      <StyledPanelResizeHandle variant="vertical" />
      <Panel defaultSize={60} minSize={40}>
        <PanelGroup direction="vertical">
          <Panel defaultSize={50} minSize={40}>
            <div style={{ width: '100%', height: '100%' }} ref={primaryRef}>
              <DataCentricPrimaryTable
                model={primaryModel}
                titleToken={primaryTitleToken}
                view={primaryView}
                baseRecordPath={baseRecordPath}
                onLoad={handlePrimaryRecordsLoad}
                onRowAction={onCustomTableAction}
                allowRowAction={allowCustomTableAction}
                onRecordSelect={handlePrimaryRecordSelect}
                selectedRecordId={selectedPrimaryRecordId}
                setAllowImages={setAllowImages}
                imagesEnabled={imagesButtonEnabled}
                allowAdd={primaryAllowAdd}
                allowView={primaryAllowView}
                allowEdit={primaryAllowEdit}
                allowDelete={primaryAllowDelete}
                allowShowImages={allowShowImages}
                allowExportShape={primaryAllowExportShape}
                allowExportDxf={primaryAllowExportDxf}
                allowDocumentButton={primaryAllowDocumentButton}
                allowShow3D={primaryAllowShow3DButton}
                dialogComponent={primaryDialogComponent}
                dialogForm={primaryDialogForm}
                dialogAsPagePath={primaryDialogAsPagePath}
                queryAttribute={primaryQueryAttribute}
                tableIdentifier={tableIdentifier ? `${tableIdentifier}_primary` : undefined}
                allowPrimaryModelActivate={allowPrimaryModelActivate}
                geometryChangeType={geometryChangeType}
                mapId={mapId}
                onGeomChange={() => setGeomChanged(true)}
                triggerRefresh={refreshPrimaryRecords}
                setTriggerRefresh={setRefreshPrimaryrecords}
                allowShowDeleted={primaryAllowShowDeleted}
              />
              {allowShowImages && showImages ? (
                <DataCentricImagesOverlay
                  closeOverlay={() => setShowImages(false)}
                  images={images}
                  selectedObjektId={selectedPrimaryRecordId}
                />
              ) : null}
            </div>
          </Panel>
          <StyledPanelResizeHandle variant="horizontal" />
          <Panel defaultSize={50} minSize={35}>
            <DataCentricSecondaryTable
              selectedPrimaryRecordId={selectedPrimaryRecordId}
              selectedRecordId={autoSelectSecondaryFromMapFeatureId ? selectedSecondaryRecordId : undefined}
              selectedPrimaryRecordQueryAttribute={primaryQueryAttribute ? selectedPrimaryQueryAttribute : undefined}
              onRecordSelect={handleSecondaryRecordSelect}
              onLoad={handleSecondaryRecordsLoad}
              view={secondaryView}
              model={secondaryModel}
              titleToken={secondaryTitleToken}
              baseRecordPath={secondaryBaseRecordPath ?? baseRecordPath}
              childRecordRelativePath={childRecordRelativePath}
              triggerRefresh={refreshSecondaryrecords}
              setTriggerRefresh={setRefreshSecondaryRecords}
              allowView={secondaryAllowView}
              allowAdd={secondaryAllowAdd}
              allowEdit={secondaryAllowEdit}
              allowDelete={secondaryAllowDelete}
              allowDocumentButton={secondaryAllowDocumentButton}
              dialogComponent={secondaryDialogComponent}
              dialogForm={secondaryDialogForm}
              tableIdentifier={tableIdentifier ? `${tableIdentifier}_secondary` : undefined}
              foreignKeyValue={selectedPrimaryRecordId && foreignKeyField ? { [foreignKeyField]: selectedPrimaryRecordId } : {}}
              allowColumnPicker={secondaryAllowColumnPicker}
              allowFilter={secondaryAllowFilter}
              allowExport={secondaryAllowExport}
              setTriggerRefreshPrimary={refreshPriamryOnSecondaryRefresh ? setRefreshPrimaryrecords : undefined}
              allowShowDeleted={secondaryAllowShowDeleted}
              onRowAction={secondaryOnCustomTableAction}
              allowRowAction={secondaryAllowCustomTableAction}
            />
          </Panel>
        </PanelGroup>
      </Panel>
    </PanelGroup>
  );
};

export default DataCentricPage;
